import {useMutation} from '@apollo/client'
import {IconButton, ListItemIcon, ListItemText, Menu, MenuItem} from '@mui/material'
import gql from 'graphql-tag'
import {FC, useState} from 'react'
import {useTranslate} from 'react-admin'

import {
  ArchiveIcon,
  EditIcon,
  MoreVertIcon,
  PictureIcon,
  UnarchiveIcon,
} from '../../components/icons'
import useUpdateChatQueries from '../../hooks/chat/useUpdateChatsQueries'
import {Chats, MutationRoot} from '../../types/graphqlSchema'
import {CHAT_FIELDS} from '.'
import ChatEditDialog from './ChatEditDialog'

const ChatContextMenuButton: FC<ChatContextMenuButtonProps> = ({
  chat: {chatUsers, id, isArchived, name},
  onOpenChatMessageFileAttachmentListSidebar,
  ...rest
}) => {
  const updateQueries = useUpdateChatQueries(id, CHAT_FIELDS)
  const [archive] = useMutation<MutationRoot['update_chats_by_pk']>(
    UPDATE_CHAT_MUTATION,
    {
      optimisticResponse: {
        // @ts-ignore
        update_chats_by_pk: {__typename: 'chats', id, isArchived: !isArchived},
      },
      updateQueries,
      variables: {id, isArchived: !isArchived},
    }
  )
  const [anchorElement, setAnchorElement] = useState<HTMLElement>()
  const [isChatNameEditDialogOpen, setIsChatNameEditDialogOpen] = useState(false)
  const closeMenu = event => {
    event.stopPropagation()
    setAnchorElement(undefined)
  }
  const translate = useTranslate()
  return (
    <>
      <IconButton
        aria-controls="long-menu"
        aria-haspopup="true"
        aria-label="more"
        onClick={e => {
          e.preventDefault()
          setAnchorElement(e.currentTarget)
        }}
        {...rest}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        keepMounted
        onClose={closeMenu}
        open={!!anchorElement}
      >
        <MenuItem
          onClick={e => {
            closeMenu(e)
            archive()
          }}
        >
          <ListItemIcon>
            {isArchived ?
              <UnarchiveIcon color="primary" /> :
              <ArchiveIcon color="primary" />
            }
          </ListItemIcon>
          <ListItemText>
            {isArchived ?
              translate('chat.internal.contextMenu.unarchive') :
              translate('chat.internal.contextMenu.archive')
            }
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={e => {
            closeMenu(e)
            setIsChatNameEditDialogOpen(true)
          }}
        >
          <ListItemIcon><EditIcon color="primary" /></ListItemIcon>
          <ListItemText>{translate('chat.internal.contextMenu.editChat')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={e => {
            closeMenu(e)
            onOpenChatMessageFileAttachmentListSidebar()
          }}
        >
          <ListItemIcon><PictureIcon color="info" /></ListItemIcon>
          <ListItemText>
            {translate('chat.internal.contextMenu.viewAllMedia')}
          </ListItemText>
        </MenuItem>
      </Menu>
      <ChatEditDialog
        chat={{chatUsers, id} as Chats}
        initialValue={name}
        onClose={() => setIsChatNameEditDialogOpen(false)}
        open={isChatNameEditDialogOpen}
      />
    </>
  )
}

const UPDATE_CHAT_MUTATION = gql`
  mutation($id: uuid!, $isArchived: Boolean!) {
    update_chats_by_pk(
      pk_columns: {id: $id}, _set: {isArchived: $isArchived}
    ){id isArchived}
  }
`

interface ChatContextMenuButtonProps {
  chat: Chats
  className?: string
  onOpenChatMessageFileAttachmentListSidebar: VoidFunction
}

export default ChatContextMenuButton
